'use client'

import { useEffect } from 'react'
import { reportClientLog } from '@/lib/client-logger'

/**
 * Installs global browser error handlers and ships anything they catch to the
 * durable log sink. Mounted once in the root layout.
 *
 * Covers the gaps route-level `error.tsx` boundaries cannot see:
 * - errors thrown in async callbacks / event handlers / timers,
 * - unhandled promise rejections,
 * - resource load failures (script/img/css) — the classic signature of a
 *   blocking browser extension, which is exactly the "works for us, not for
 *   them" case. Resource errors don't bubble, so we listen in the capture phase.
 */
export function ClientErrorReporter() {
  useEffect(() => {
    function onError(event: ErrorEvent) {
      const target = event.target as (HTMLElement & { src?: string; href?: string }) | null
      const isResourceError = !event.message && target != null && target !== (window as unknown as EventTarget)
      if (isResourceError) {
        const url = target?.src ?? target?.href
        reportClientLog({
          source: 'resource.error',
          message: `Resource failed to load: ${target?.tagName?.toLowerCase() ?? 'unknown'} ${url ?? ''}`.trim(),
        })
        return
      }
      reportClientLog({
        source: 'window.onerror',
        message: event.message || 'Uncaught error',
        errorName: event.error?.name,
        stack: event.error?.stack,
      })
    }

    function onRejection(event: PromiseRejectionEvent) {
      const reason = event.reason
      const message =
        (reason && typeof reason === 'object' && 'message' in reason
          ? String((reason as { message: unknown }).message)
          : String(reason)) || 'Unhandled promise rejection'
      reportClientLog({
        source: 'unhandledrejection',
        message,
        errorName: reason?.name,
        stack: reason?.stack,
      })
    }

    // Capture phase (true) so non-bubbling resource load errors are seen too.
    window.addEventListener('error', onError, true)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError, true)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  return null
}
